import React, { useEffect, useRef, useState } from 'react';
import { Header } from '../Header';
import { Footer } from '../Footer';
import { useMarketplaceExperiment, useUserContext } from '../../contexts';
import featureFlags from '../../lib/featureFlags';
import {
    buildMarketplaceContentReturnUrl,
    getMarketplaceCartController,
    isSecondaryCrateHost,
    MARKETPLACE_CART_RETURN_PARAM,
} from '../../lib/marketplaceCart';
import '../../styles/layout.scss';
import './MarketplaceCart.scss';

const parsePositiveInteger = (value) => {
    const parsedValue = Number.parseInt(String(value ?? ''), 10);
    if (!Number.isInteger(parsedValue) || parsedValue <= 0) {
        return null;
    }
    return parsedValue;
};

const isValidMarketplaceHandoff = (searchParams) => {
    if (
        searchParams.get('marketplace_experiment') !== 'marketplace_v1'
        || searchParams.get('marketplace_cohort') !== 'B'
    ) {
        return false;
    }

    try {
        const originUrl = new URL(String(searchParams.get('origin_url') ?? '').trim());
        return isSecondaryCrateHost(originUrl.hostname);
    } catch (error) {
        return false;
    }
};

const MarketplaceCartIntake = () => {
    const { user, isAuthenticating } = useUserContext();
    const {
        isResolved,
        marketplaceEnabled,
        promoteMarketplaceToB,
    } = useMarketplaceExperiment();
    const [retryKey, setRetryKey] = useState(0);
    const [promotionError, setPromotionError] = useState(false);
    const processedAttemptRef = useRef(null);

    useEffect(() => {
        if (isAuthenticating || !featureFlags.marketplaceCart) {
            if (!isAuthenticating && !featureFlags.marketplaceCart) {
                window.location.replace('/');
            }
            return;
        }

        const searchParams = new URLSearchParams(window.location.search);
        const hasValidHandoff = isValidMarketplaceHandoff(searchParams);

        if (!hasValidHandoff && !isResolved) {
            return;
        }

        if (processedAttemptRef.current === retryKey) {
            return;
        }
        processedAttemptRef.current = retryKey;

        const type = String(searchParams.get('type') ?? '').trim().toLowerCase();
        const name = String(searchParams.get('name') ?? '').trim();
        const amount = Number(searchParams.get('amount'));
        const currency = String(searchParams.get('currency') ?? '').trim();
        const vertical = String(searchParams.get('vertical') ?? '').trim();
        const previewUrl = String(
            searchParams.get('preview_url')
            ?? searchParams.get('previewUrl')
            ?? searchParams.get('thumbnail_url')
            ?? searchParams.get('thumbnailUrl')
            ?? ''
        ).trim();
        const waveformUrl = String(
            searchParams.get('waveform_url')
            ?? searchParams.get('waveformUrl')
            ?? searchParams.get('waveform_json_url')
            ?? searchParams.get('waveformJsonUrl')
            ?? ''
        ).trim();
        const originUrl = String(searchParams.get('origin_url') ?? '').trim();
        const assetPriceId = parsePositiveInteger(searchParams.get('asset_price_id'));
        const assetPriceSourceType = String(searchParams.get('asset_price_source_type') ?? '').trim().toLowerCase();
        const hasAssetPrice = Boolean(assetPriceId && assetPriceSourceType === 'asset_price');

        const buildDisplay = () => {
            if (!name && !Number.isFinite(amount) && !currency && !vertical && !previewUrl && !waveformUrl) {
                return undefined;
            }

            return {
                name,
                amount: Number.isFinite(amount) ? amount : undefined,
                currency,
                vertical,
                previewUrl,
                waveformUrl,
            };
        };

        const redirectToCart = (suffix) => {
            window.location.replace(`/marketplace/cart${suffix}`);
        };

        const buildContentReturnUrl = () => {
            const contentReturnUrl = buildMarketplaceContentReturnUrl(originUrl, window);
            if (!contentReturnUrl) {
                return null;
            }

            const destinationUrl = new URL(contentReturnUrl);
            destinationUrl.searchParams.set(MARKETPLACE_CART_RETURN_PARAM, '1');
            return destinationUrl.toString();
        };

        const redirectToSafeDestination = () => {
            window.location.replace('/');
        };

        const continueIntake = async () => {
            let payload = null;
            if (type === 'asset') {
                const assetUuid = String(searchParams.get('asset_uuid') ?? '').trim();
                if (assetUuid) {
                    payload = {
                        type: 'asset',
                        assetUuid,
                        assetPriceId: hasAssetPrice ? assetPriceId : undefined,
                        assetPriceSourceType: hasAssetPrice ? 'asset_price' : undefined,
                        display: buildDisplay(),
                        sourceContext: {
                            explicitPurchase: searchParams.get('open') === '1',
                            originUrl,
                            originDomain: '',
                        },
                    };
                }
            }

            if (type === 'bundle') {
                const bundleId = parsePositiveInteger(searchParams.get('bundle_id'));
                if (bundleId) {
                    payload = {
                        type: 'bundle',
                        bundleId,
                        display: buildDisplay(),
                        sourceContext: {
                            explicitPurchase: searchParams.get('open') === '1',
                            originUrl,
                            originDomain: '',
                        },
                    };
                }
            }

            if (!payload) {
                redirectToCart('?intake=invalid');
                return;
            }

            if (hasValidHandoff) {
                try {
                    await promoteMarketplaceToB();
                } catch (error) {
                    setPromotionError(true);
                    return;
                }
            } else if (!marketplaceEnabled) {
                redirectToSafeDestination();
                return;
            }

            const marketplaceCartController = getMarketplaceCartController(window);
            marketplaceCartController.setSession({
                user: user || null,
                isAuthenticating: false,
            });

            try {
                await marketplaceCartController.waitForSessionResolution();
            } catch (error) {
                setPromotionError(true);
                return;
            }

            try {
                await window.CrateCart.addItem(payload);
            } catch (error) {
                redirectToCart('?intake=invalid');
                return;
            }

            const contentReturnUrl = hasValidHandoff ? buildContentReturnUrl() : null;
            if (contentReturnUrl) {
                window.location.replace(contentReturnUrl);
                return;
            }

            redirectToCart('?intake=return_unavailable');
        };

        continueIntake();
    }, [
        isAuthenticating,
        isResolved,
        marketplaceEnabled,
        promoteMarketplaceToB,
        retryKey,
        user,
    ]);

    const retryPromotion = () => {
        processedAttemptRef.current = null;
        setPromotionError(false);
        setRetryKey((currentRetryKey) => currentRetryKey + 1);
    };

    return (
        <div className="layout marketplace-cart-page">
            <Header />
            <main className="marketplace-cart-page__body is-intake">
                <section className="marketplace-cart-page__shell">
                    <div className="marketplace-cart-page__intro">
                        <div className="marketplace-cart-page__eyebrow">Marketplace cart</div>
                        <h1>{promotionError ? 'Unable to open your cart' : 'Adding item to cart'}</h1>
                        <p>
                            {promotionError
                                ? 'Please retry to continue with your ProductionCrate cart.'
                                : 'We are moving your selection into the shared ProductionCrate cart.'}
                        </p>
                        {promotionError && (
                            <button type="button" onClick={retryPromotion}>Retry</button>
                        )}
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
};

export default MarketplaceCartIntake;
